import {
  assertReleaseBundleProfileRoles,
  parseReleaseBundleManifest,
  verifyReleaseBundleProfile,
} from '@crabs-ai/alphabet-writing-runtime/release-bundle';
import {parseAlphabetWritingWordLayoutPolicy} from '@crabs-ai/alphabet-writing-runtime/contracts';
import {
  assertApprovedWritingGuideRuntimeMatchesRelease,
  type ApprovedWritingGuideRelease,
} from '../../vendor/src/features/remotion-editor/editor/items/code/approved-writing-guide-release';
import {pinnedWritingBundle} from './pinned-writing-guide-bundles';

const TIMEOUT_MS = 15_000;
const MAX_MANIFEST_BYTES = 24 * 1024; // Shipped manifests are 15,416 bytes.
const MAX_ROLE_BYTES: Record<string, number> = {
  runtime: 1024 * 1024, // Largest shipped runtime is 985,989 bytes.
  renderer: 128 * 1024, // Largest shipped renderer is 116,083 bytes.
  wordLayoutPolicy: 1024, // Largest shipped policy is 317 bytes.
};

async function digest(bytes: Uint8Array<ArrayBuffer>): Promise<string> {
  if (!globalThis.crypto?.subtle) throw Error('따라쓰기 번들 바이트 검증을 사용할 수 없습니다.');
  const value = await crypto.subtle.digest('SHA-256', bytes);
  return `sha256:${[...new Uint8Array(value)].map(byte => byte.toString(16).padStart(2, '0')).join('')}`;
}

/** Bounds both response headers and streamed bytes; timeout covers headers and body. */
export async function fetchBoundedWritingBundleBytes(url: string, maxBytes: number): Promise<Uint8Array<ArrayBuffer>> {
  const controller = new AbortController();
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      void reader?.cancel().catch(() => undefined);
      reject(Error('승인된 따라쓰기 번들 요청 시간이 초과됐습니다.'));
    }, TIMEOUT_MS);
  });
  try {
    return await Promise.race([(async () => {
      const response = await fetch(url, {cache: 'no-store', signal: controller.signal});
      if (!response.ok || !response.body) throw Error(`승인된 따라쓰기 번들 파일을 읽을 수 없습니다. (${response.status})`);
      const headerLength = response.headers.get('content-length');
      if (headerLength && Number(headerLength) > maxBytes) {
        controller.abort();
        throw Error('승인된 따라쓰기 번들 파일 크기가 허용 범위를 넘었습니다.');
      }
      reader = response.body.getReader();
      const chunks: Uint8Array[] = [];
      let length = 0;
      while (true) {
        const {done, value} = await reader.read();
        controller.signal.throwIfAborted();
        if (done) break;
        length += value.byteLength;
        if (length > maxBytes) {
          controller.abort();
          void reader.cancel().catch(() => undefined);
          throw Error('승인된 따라쓰기 번들 파일 크기가 허용 범위를 넘었습니다.');
        }
        chunks.push(value);
      }
      const bytes = new Uint8Array(new ArrayBuffer(length));
      let offset = 0;
      for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
      return bytes;
    })(), timeout]);
  } finally {
    if (timer) clearTimeout(timer);
    if (reader) {
      try { reader.releaseLock(); } catch { /* A cancelled read may still be pending. */ }
    }
  }
}

/** Shared preview/export loader. Export first pins the project manifest via readTrustedWritingGuideSpec. */
export async function loadBundleWritingGuideRuntimeAsset(
  release: ApprovedWritingGuideRelease,
  assetUrl: (path: string) => string,
): Promise<Record<string, unknown>> {
  if (!release.bundleContentHash || !release.bundleManifestAssetPath || !release.wordLayoutPolicyAssetPath)
    throw Error(`승인된 따라쓰기 번들 명세가 없습니다. (${release.releaseId})`);
  const pin = pinnedWritingBundle(release.bundleContentHash);
  const manifestBytes = await fetchBoundedWritingBundleBytes(assetUrl(release.bundleManifestAssetPath), MAX_MANIFEST_BYTES);
  if (pin && (manifestBytes.byteLength !== pin.manifestByteLength || await digest(manifestBytes) !== pin.manifestSha256))
    throw Error(`승인된 따라쓰기 번들 명세 바이트가 출하본과 다릅니다. (${release.releaseId})`);
  let manifestValue: unknown;
  try { manifestValue = JSON.parse(new TextDecoder().decode(manifestBytes)); }
  catch { throw Error(`승인된 따라쓰기 번들 명세 JSON이 올바르지 않습니다. (${release.releaseId})`); }
  const manifest = parseReleaseBundleManifest(manifestValue);
  if (manifest.bundleContentHash !== release.bundleContentHash ||
      manifest.releaseId !== release.releaseId ||
      manifest.alphabetCase !== release.alphabetCase)
    throw Error(`승인된 따라쓰기 번들 바인딩이 명세와 다릅니다. (${release.releaseId})`);
  const suffix = '/manifest.v1.json';
  if (!release.bundleManifestAssetPath.endsWith(suffix))
    throw Error(`승인된 따라쓰기 번들 경로가 올바르지 않습니다. (${release.releaseId})`);
  const root = release.bundleManifestAssetPath.slice(0, -suffix.length);
  assertReleaseBundleProfileRoles(manifest, 'webRuntime', ['renderer', 'runtime', 'wordLayoutPolicy']);
  const bytesByPath = new Map<string, Uint8Array<ArrayBuffer>>();
  await verifyReleaseBundleProfile({
    manifest,
    profile: 'webRuntime',
    readFile: async relativePath => {
      const cached = bytesByPath.get(relativePath);
      if (cached) return cached;
      const role = manifest.profiles.webRuntime.find(candidate => manifest.files[candidate]?.path === relativePath);
      const limit = role ? MAX_ROLE_BYTES[role] : undefined;
      const expectedBytes = role ? manifest.files[role]?.byteLength : undefined;
      if (!limit || expectedBytes === undefined || expectedBytes > limit)
        throw Error(`승인된 따라쓰기 번들 역할 크기가 올바르지 않습니다. (${release.releaseId})`);
      const bytes = await fetchBoundedWritingBundleBytes(assetUrl(`${root}/${relativePath}`), Math.min(limit, expectedBytes));
      bytesByPath.set(relativePath, bytes);
      return bytes;
    },
  });
  const runtimeFile = manifest.files.runtime;
  const policyFile = manifest.files.wordLayoutPolicy;
  if (!runtimeFile || !policyFile) throw Error(`승인된 따라쓰기 번들 파일 구성이 올바르지 않습니다. (${release.releaseId})`);
  let runtime: unknown;
  let policy: unknown;
  try {
    runtime = JSON.parse(new TextDecoder().decode(bytesByPath.get(runtimeFile.path)));
    policy = JSON.parse(new TextDecoder().decode(bytesByPath.get(policyFile.path)));
  } catch { throw Error(`승인된 따라쓰기 번들 JSON이 올바르지 않습니다. (${release.releaseId})`); }
  if (!runtime || typeof runtime !== 'object' || Array.isArray(runtime))
    throw Error(`승인된 따라쓰기 runtime JSON이 올바르지 않습니다. (${release.releaseId})`);
  const approvedRuntime = runtime as Record<string, unknown>;
  assertApprovedWritingGuideRuntimeMatchesRelease(approvedRuntime, release);
  try { return {...approvedRuntime, wordLayoutPolicy: parseAlphabetWritingWordLayoutPolicy(policy)}; }
  catch { throw Error(`승인된 따라쓰기 레이아웃 정책이 올바르지 않습니다. (${release.releaseId})`); }
}
